"use client"

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import { HugeiconsIcon } from "@hugeicons/react"
import { ArrowUpDoubleIcon, UserCircleIcon, Settings05Icon, LogoutIcon, Moon02Icon, Sun03Icon, LaptopIcon } from "@hugeicons/core-free-icons"
import { useNavigate } from "react-router-dom"
import { useUser } from "@/auth/UserContext"
import { useAuth } from "@/auth/useAuth"
import { useWorkspace } from "@/workspaces/WorkspaceContext"
import { useTheme } from "@/components/theme-provider"
import { resolveAssetUrl } from "@/lib/asset-url"

function userInitials(name: string | null, email: string) {
  if (name) {
    return name.split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("")
  }
  return email.slice(0, 2).toUpperCase()
}

export function NavUser() {
  const { user } = useUser()
  const { logout } = useAuth()
  const { workspace } = useWorkspace()
  const navigate = useNavigate()
  const { theme, setTheme } = useTheme()

  if (!user) return null

  const displayName = user.name ?? user.email
  const initials = userInitials(user.name, user.email)

  return (
    <SidebarMenu className="p-0">
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              style={{ paddingTop: 0, paddingBottom: 0 }}
              className="h-[43px] px-3 rounded-none data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={resolveAssetUrl(user.avatar_url)} alt={displayName} />
                <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{displayName}</span>
                <span className="truncate text-xs text-muted-foreground">{user.email}</span>
              </div>
              <HugeiconsIcon icon={ArrowUpDoubleIcon} strokeWidth={2} className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side="top"
            align="end"
            sideOffset={4}
            alignOffset={-8}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={resolveAssetUrl(user.avatar_url)} alt={displayName} />
                  <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{displayName}</span>
                  <span className="truncate text-xs text-muted-foreground">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => navigate(`/${workspace.slug}/settings/profile`)}>
                <HugeiconsIcon icon={UserCircleIcon} strokeWidth={2} />
                Мой профиль
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/${workspace.slug}/settings/notifications`)}>
                <HugeiconsIcon icon={Settings05Icon} strokeWidth={2} />
                Настройки уведомлений
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5">
              <p className="text-xs text-muted-foreground mb-2">Тема</p>
              <div className="flex gap-1">
                {([
                  { value: "light", icon: Sun03Icon, label: "Светлая" },
                  { value: "system", icon: LaptopIcon, label: "Авто" },
                  { value: "dark", icon: Moon02Icon, label: "Тёмная" },
                ] as const).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setTheme(opt.value)}
                    title={opt.label}
                    className={`flex-1 flex items-center justify-center py-1.5 rounded-md transition-colors ${
                      theme === opt.value
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-accent hover:text-foreground"
                    }`}
                  >
                    <HugeiconsIcon icon={opt.icon} strokeWidth={2} className="size-3.5" />
                  </button>
                ))}
              </div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout}>
              <HugeiconsIcon icon={LogoutIcon} strokeWidth={2} />
              Выйти
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
